import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronUp, IoChevronDown, IoMailOutline, IoChatbubbleEllipsesOutline, IoQrCodeOutline } from 'react-icons/io5';
import InfoTooltip from '../../../../components/common/InfoTooltip';
import { requestShare } from '../../../../bridge/webBridge';
import { useAuth } from '../../../../context/AuthContext';
import {
  getRealtorTemplate, getHqDefaultTemplate, saveRealtorTemplate, renderTemplate, FALLBACK_TEMPLATE,
} from '../../../../services/inviteMessageService';

const buildFormUrl = (uid) => `https://ieum-landing.web.app/apply?ref=${uid || 'demo'}`;

const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const CustomerFormSection = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [msgText, setMsgText] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const formUrl = buildFormUrl(userData?.uid);
  const orgName = userData?.businessName || userData?.business_name || userData?.name || '부동산';

  useEffect(() => {
    let alive = true;
    (async () => {
      const mine = await getRealtorTemplate(userData?.uid);
      if (!alive) return;
      if (mine) {
        const rendered = renderTemplate(mine, { orgName, formUrl });
        setMsgText(rendered);
        setSavedMsg(rendered);
        return;
      }
      const hq = await getHqDefaultTemplate();
      const tmpl = hq || FALLBACK_TEMPLATE;
      const rendered = renderTemplate(tmpl, { orgName, formUrl });
      if (!alive) return;
      setMsgText(rendered);
      setSavedMsg(rendered);
    })();
    return () => { alive = false; };
  }, [userData?.uid, formUrl, orgName]);

  const handleSaveTemplate = async () => {
    if (!userData?.uid || msgText === savedMsg || saving) return;
    setSaving(true);
    try {
      const templated = msgText
        .split(formUrl).join('{formUrl}')
        .split(`"${orgName}"`).join('"{orgName}"');
      await saveRealtorTemplate(userData.uid, templated);
      setSavedMsg(msgText);
    } catch (_) {
      /* skip */
    } finally {
      setSaving(false);
    }
  };

  const handleSms = async () => {
    const rawPhone = phone.replace(/\D/g, '');
    if (!rawPhone) {
      alert('휴대폰 번호를 입력해주세요.');
      return;
    }
    await handleSaveTemplate();
    const body = encodeURIComponent(msgText);
    window.location.href = `sms:${rawPhone}?body=${body}`;
  };

  const handleKakao = async () => {
    await handleSaveTemplate();
    const shared = requestShare({
      title: '이음 - 이사 서비스 무료 견적',
      text: msgText,
    });
    if (!shared) {
      navigator.clipboard.writeText(msgText).then(() => {
        alert('링크가 클립보드에 복사되었습니다.\n카카오톡에 붙여넣기 해주세요.');
      });
    }
  };

  return (
    <section>
      <FormCard>
        <SectionTitle>고객 폼메일 발송 <InfoTooltip text={`단 5초만에 고객을 초대하세요. 고객 정보를 입력한 뒤 문자나 카톡 발송을 누르면 ${orgName} 전용 초대 메시지가 발송됩니다.`} /></SectionTitle>
        <Desc>
          이름과 전화번호만 입력한 뒤 문자 또는 카톡으로 보내면 기본 문구와 폼 링크가 자동으로 발송됩니다.
        </Desc>

        <FormGroup>
          <Label>이름 (선택)</Label>
          <Input
            placeholder="홍길동"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>휴대폰 번호</Label>
          <Input
            placeholder="010-1234-5678"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            inputMode="tel"
          />
        </FormGroup>

        <EditToggle onClick={() => setShowPreview(!showPreview)}>
          {showPreview ? <IoChevronUp size={14} /> : <IoChevronDown size={14} />}
          {showPreview ? '문구 편집 접기' : '문구 편집 펼치기'}
        </EditToggle>

        {showPreview && (
          <EditTextarea
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            onBlur={handleSaveTemplate}
          />
        )}

        <BtnRow>
          <ActionBtn onClick={handleSms}><IoMailOutline size={16} /> 문자 발송</ActionBtn>
          <ActionBtn $variant="kakao" onClick={handleKakao}><IoChatbubbleEllipsesOutline size={16} /> 카톡 발송</ActionBtn>
        </BtnRow>

        <QrBtn onClick={() => navigate('/realtor/recommend/qr')}>
          <IoQrCodeOutline size={16} /> 전용 QR코드 보기
        </QrBtn>
      </FormCard>
    </section>
  );
};

export default CustomerFormSection;

// ───── 스타일 (InviteCustomer 의 동일 styled 복사) ─────

const FormCard = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const EditToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  resize: vertical;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const BtnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ActionBtn = styled.button`
  padding: 14px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ $variant }) => ($variant === 'kakao' ? '#3C1E1E' : '#fff')};
  background: ${({ $variant, theme }) => ($variant === 'kakao' ? '#FEE500' : theme.colors.primary)};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const QrBtn = styled.button`
  width: 100%;
  padding: 14px;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;
